import {useState, useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import {
    ChartBarIcon,
    PlusCircleIcon,
    ArchiveBoxIcon,
    DocumentTextIcon,
    ArrowRightStartOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

interface ElementNavigation {
    etiquette: string;
    chemin: string;
    icone: React.ComponentType<{className?: string}>;
}

const elementsNavigation: ElementNavigation[] = [
    {etiquette: "Tableau de bord", chemin: "/tableau", icone: ChartBarIcon},
    {etiquette: "Nouvelle réunion", chemin: "/nouvelle-reunion", icone: PlusCircleIcon},
    {etiquette: "Archives", chemin: "/archives", icone: ArchiveBoxIcon},
    {etiquette: "Templates", chemin: "/templates", icone: DocumentTextIcon},
];

export default function Sidebar() {
    const naviguer = useNavigate();
    const localisation = useLocation();
    const {seDeconnecter} = useAuth();
    const [reduit, setReduit] = useState<boolean>(false);

    useEffect(() => {
        const gererRedimensionnement = (): void => {
            setReduit(window.innerWidth < 1024);
        };
        gererRedimensionnement();
        window.addEventListener("resize", gererRedimensionnement);
        return () => window.removeEventListener("resize", gererRedimensionnement);
    }, []);

    const gererDeconnexion = async (): Promise<void> => {
    await seDeconnecter();
    naviguer("/connexion");
    };

    return (
        <div className={`flex flex-col h-screen bg-white border-r border-gray-100 shadow-sm transition-all duration-300 ${reduit ? "w-16" : "w-64"}`}>

            {/* ── En-tête */}
            <div className={`flex items-center h-16 px-3 border-b border-gray-100 ${reduit ? "justify-center" : "justify-between"}`}>
                {!reduit && (
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                            style={{background: "linear-gradient(135deg, #6B21A8, #F97316)"}}>
                            G
                        </div>
                        <div>
                            <h1 className="text-sm font-bold" style={{
                                background: "linear-gradient(90deg, #6B21A8, #F97316)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent"
                            }}>GMITIX</h1>
                            <p className="text-xs text-gray-400">Gestion de réunions</p>
                        </div>
                    </div>
                )}
                <button
                    onClick={() => setReduit(!reduit)}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                >
                    {reduit ? <Bars3Icon className="w-5 h-5"/> : <XMarkIcon className="w-5 h-5"/>}
                </button>
            </div>

            {/* ── Navigation */}
            <nav className="flex flex-col gap-1 flex-1 p-3">
                {elementsNavigation.map((element) => {
                    const estActif = localisation.pathname === element.chemin;
                    return (
                        <button
                            key={element.chemin}
                            onClick={() => naviguer(element.chemin)}
                            title={reduit ? element.etiquette : undefined}
                            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium ${
                                reduit ? "justify-center" : "justify-start"
                            } ${estActif
                                ? "text-white shadow-md"
                                : "text-gray-500 hover:bg-purple-50 hover:text-purple-700"
                            }`}
                            style={estActif ? {background: "linear-gradient(90deg, #6B21A8, #9333EA)"} : {}}
                        >
                            <element.icone className="w-5 h-5 shrink-0"/>
                            {!reduit && <span>{element.etiquette}</span>}
                        </button>
                    );
                })}
            </nav>

            {/* ── Déconnexion */}
            <div className="p-3 border-t border-gray-100">
                <button
                    onClick={gererDeconnexion}
                    title={reduit ? "Se déconnecter" : undefined}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors ${
                        reduit ? "justify-center" : "justify-start"
                    }`}
                >
                    <ArrowRightStartOnRectangleIcon className="w-5 h-5 shrink-0"/>
                    {!reduit && <span>Se déconnecter</span>}
                </button>
            </div>
        </div>
    );
}